import { z } from "zod";

export const PAYMENT_TEST_CARD = "4242424242424242";
export const PAYMENT_TEST_ACCOUNT = "000012345678";
export const PAYMENT_TEST_ROUTING = "000000000";
const text = (max = 70) => z.string().trim().min(1, "This field is required.")
  .max(max, `Use ${max} characters or fewer.`)
  .regex(/^[\x20-\x7E]+$/, "Use standard English letters, numbers and punctuation for this test.");
const fixed = <T extends string | boolean>(value: T, message: string) =>
  z.literal(value, { errorMap: () => ({ message }) });

const common = {
  reference: z.string().uuid(),
  companyName: text(65),
  signerName: text(45),
  title: text(30),
  signature: text(45),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Choose a valid signing date.")
    .refine(value => !Number.isNaN(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value, "Choose a valid signing date."),
  terms: z.enum(["Due On Receipt", "NET 15", "NET 30"]),
  consent: fixed(true, "Acknowledge the test electronic signature."),
  syntheticOnly: fixed(true, "Confirm that you are using synthetic test information only."),
  emailCopy: z.boolean(),
};
export const paymentInformationTestSchema = z.discriminatedUnion("method", [
  z.object({
    ...common, method: z.literal("credit-card"),
    cardholder: text(65),
    cardNumber: z.string().transform(value => value.replace(/[\s-]/g, ""))
      .pipe(fixed(PAYMENT_TEST_CARD, "Testing accepts only 4242 4242 4242 4242. Do not enter a real card.")),
    expiration: z.string().regex(/^(0[1-9]|1[0-2])\/20\d{2}$/, "Use MM/YYYY."),
    cvv: fixed("000", "Use the dummy CVV 000 only."),
    feeAcknowledgment: fixed(true, "Acknowledge the credit-card fee note for this test."),
  }).strict(),
  z.object({
    ...common, method: z.literal("ach"),
    accountType: z.enum(["Checking", "Savings"]),
    accountHolder: text(65), bankName: text(65), bankAddress: text(65), bankCityStateZip: text(65),
    accountNumber: fixed(PAYMENT_TEST_ACCOUNT, `Use the dummy account number ${PAYMENT_TEST_ACCOUNT} only.`),
    routingNumber: fixed(PAYMENT_TEST_ROUTING, `Use the dummy routing number ${PAYMENT_TEST_ROUTING} only.`),
  }).strict(),
]).superRefine((value, ctx) => {
  if (value.signature.toLowerCase() !== value.signerName.toLowerCase()) {
    ctx.addIssue({ code: "custom", path: ["signature"], message: "Type the same name as the printed signer name." });
  }
});
export type PaymentInformationTest = z.infer<typeof paymentInformationTestSchema>;
